import { useState, useEffect, useId } from "react";
import { useTranslation } from "@/packages/utils";
import { ConfirmDialog } from "../ConfirmDialog";
import { ConfirmDialogTitle } from "../ConfirmDialog/ConfirmDialogTitle";
import { ConfirmDialogBody } from "../ConfirmDialog/ConfirmDialogBody";
import { ConfirmDialogActions } from "../ConfirmDialog/ConfirmDialogActions";
import { Input } from "../primitives/Input";
import { TagColorPicker } from "./TagColorPicker";
import type { TagFormDialogProps } from "./types";

export function TagFormDialog({
  open,
  onClose,
  mode,
  initialData,
  onSubmit,
}: TagFormDialogProps) {
  const { t } = useTranslation(["common"]);
  const id = useId();
  const [name, setName] = useState(initialData?.name ?? "");
  const [color, setColor] = useState(initialData?.color ?? "#3b82f6");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialData?.name ?? "");
      setColor(initialData?.color ?? "#3b82f6");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setLoading(true);
    try {
      await onSubmit({ name: trimmedName, color });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setName(initialData?.name ?? "");
    setColor(initialData?.color ?? "#3b82f6");
    onClose();
  };

  return (
    <ConfirmDialog open={open} onClose={handleCancel}>
      <ConfirmDialogTitle>
        {mode === "create"
          ? t("common:tags.createTag")
          : t("common:tags.editTag")}
      </ConfirmDialogTitle>
      <ConfirmDialogBody>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label
              htmlFor={id}
              className="text-[13px] font-medium text-primary"
            >
              {t("common:labels.name")}
            </label>
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("common:tags.enterTagName")}
              autoFocus
            />
          </div>
          <TagColorPicker value={color} onChange={setColor} label="Color" />
        </div>
      </ConfirmDialogBody>
      <ConfirmDialogActions
        onConfirm={handleSubmit}
        onCancel={handleCancel}
        confirmLabel={
          mode === "create"
            ? t("common:buttons.create")
            : t("common:buttons.save")
        }
        loading={loading}
      />
    </ConfirmDialog>
  );
}
